import { useState } from 'react';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '../../../shared/FormStyles/Button';
import TileDigestsSkeleton from './TileDigestsSkeleton.jsx';
import TileDigest from './TileDigest.jsx';
import { useCampaignFilter } from '../hooks/useCampaignFilter';

import { useGetTileDigests } from './data';
import Tooltip from '../../../shared/Tooltip';
import Text from '../../../shared/Text';

const TileDigestsReports = () => {
  const [limit, setLimit] = useState(2);
  const newAirbo = useSelector(state => state.currentBoard.newAirbo);
  const { campaignId } = useCampaignFilter();

  const {
    data: {
      data: { reports },
      totalDigests,
    },
    isLoading,
    isFetched,
  } = useGetTileDigests({ limit, page: 1, campaignId });

  const { t } = useTranslation('reports');

  const handleClick = () => {
    setLimit(limit + 2);
  };

  const DigestBody = () => {
    if (newAirbo && !campaignId) {
      return (
        <Text variant="body2" as="div" padding="24px">
          {t('posts.noCampaignSelected')}
        </Text>
      );
    }

    if (!isFetched) {
      return <TileDigestsSkeleton />;
    }

    if (reports.length === 0 && isFetched) {
      return (
        <Text variant="body2" as="div" padding="24px">
          {t('posts.noDataYet')}
        </Text>
      );
    }

    return reports.map(report => {
      return <TileDigest key={report.tileEmailId} digest={report} />;
    });
  };

  const Footer = () => {
    const noDigests = reports && !reports.length;
    const maxDigests = reports && reports.length === totalDigests;

    if (isLoading || noDigests || maxDigests) return null;

    return (
      <ButtonContainer>
        <Button disabled={isLoading} variant="tertiary" onClick={handleClick}>
          {t('tileDigests.loadMore')}
        </Button>
      </ButtonContainer>
    );
  };

  return (
    <Wrapper>
      <Tooltip />

      <div>
        <Header>
          {newAirbo ? (
            <span data-tip={t('posts.headerTooltip')}>{t('posts.header')}</span>
          ) : (
            t('tileDigests.header')
          )}
        </Header>
        <Body>
          <DigestBody />
        </Body>
        <Footer />
      </div>
    </Wrapper>
  );
};

export default TileDigestsReports;

const Header = styled.div`
  background: ${themeGet('colors.white')};
  border: 1px solid ${themeGet('colors.black.10')};
  border-radius: 8px 8px 0 0;
  border-bottom: none;
  padding: 15px 29px;
  font-size: 18px;
  font-weight: 600;
  font-family: Montserrat;
  position: relative;
`;

const Body = styled.div`
  background-color: ${themeGet('colors.white')};
  border: 1px solid ${themeGet('colors.black.10')};
  border-radius: 0 0 8px 8px;
  padding: 0 24px;
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin-top: 24px;
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
